import { useCallback, useState } from 'react';

export interface SequentialFailure<T> {
  item: T;
  message: string;
}

/**
 * Creates items one at a time (so the server's own checks see each earlier
 * item), tracking progress and collecting failures instead of stopping.
 */
export const useSequentialCreate = () => {
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const run = useCallback(async <T>(items: T[], create: (item: T) => Promise<unknown>) => {
    const failed: SequentialFailure<T>[] = [];
    for (let i = 0; i < items.length; i += 1) {
      setProgress({ done: i, total: items.length });
      try {
        await create(items[i]);
      } catch (e: any) {
        failed.push({ item: items[i], message: e?.response?.data?.message || e?.message || 'Could not be added' });
      }
    }
    setProgress(null);
    return { added: items.length - failed.length, failed };
  }, []);

  return { progress, busy: progress !== null, run };
};

export default useSequentialCreate;
